import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';

import Layout from '~/src/components/layout/Layout';
import NavigationItem from '~/src/components/navigation/item/NavigationItem';
import Boomerang from '~/src/components/svg/general/Boomerang';
import ScrollListener from '~/src/components/listener/scroll/ScrollListener';

import {SitemapContext} from '~/src/components/context/SitemapContext';
import {getCategoryUrl} from '~/src/utilities/helpers/link';
import {classNames} from '~/src/utilities/helpers/css';
import {NavigationItemFormat} from '~/src/utilities/constants/formats';
import {createNavigationTracker} from '~/src/utilities/tracking/gtm';
import initPolyglot from '~/src/utilities/libs/polyglot';

import css from './NavigationSubcategory.module.scss';
import i18nStrings from './NavigationSubcategory.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const NavigationSubcategory = ({categoryId, parent = null, inTeaserSection = false}) => {
    const {getCategoryBy} = useContext(SitemapContext);

    const [isActive, setIsActive] = useState(false);
    const [isHidden, setIsHidden] = useState(false);

    const button = useRef();
    const list = useRef();

    const mapCategory = useCallback(
        (categoryToMap) => {
            const flags = {
                isActive: categoryToMap.categoryId === categoryId
            };
            return (
                <NavigationItem
                    key={categoryToMap.categoryId}
                    href={getCategoryUrl(categoryToMap.fullUrlPath)}
                    label={categoryToMap.name}
                    flags={flags}
                    format={NavigationItemFormat.NAVIGATION_ITEM_SUBCATEGORY}
                    onClick={createNavigationTracker(3, categoryToMap.name, categoryToMap.categoryId)}
                />
            );
        },
        [categoryId]
    );

    const onButtonClick = useCallback(
        (event) => {
            const clickOnButton = button.current && button.current.contains(event.target);
            const clickInList = list.current && list.current.contains(event.target);
            const timeout = clickInList ? 20 : 0;

            if (clickOnButton) {
                event.stopPropagation();
            }

            window.setTimeout(setIsActive, timeout, !isActive);
        },
        [button, list, isActive]
    );

    useEffect(() => {
        if (isActive) {
            document.body.addEventListener('click', onButtonClick);

            return () => document.body.removeEventListener('click', onButtonClick);
        }
    }, [isActive, onButtonClick]);

    const onScroll = useCallback((distance) => {
        setIsHidden(distance > 0);
    }, []);

    const Wrapper = inTeaserSection ? React.Fragment : Layout;
    const current = getCategoryBy({categoryId: categoryId});
    const category = getCategoryBy({categoryId: parent}) || current;

    const label = current !== category ? current.name : getTranslation('choose');

    const className = classNames(css, 'navigation', {isActive, isHidden, inTeaserSection});

    return (
        <div className={className} data-testid="NavigationSubcategory">
            {!inTeaserSection && <ScrollListener onScroll={onScroll} threshold={20} />}
            <Wrapper>
                <div className={css.content}>
                    <button ref={button} className={css.button} onClick={onButtonClick}>
                        {label}
                        <Boomerang />
                    </button>

                    {isActive && (
                        <div className={css.categories}>
                            <ul ref={list} className={css.list}>
                                {mapCategory({...category, name: getTranslation('all')})}
                                {category.children.filter(({visibility}) => visibility.inMenu !== false).map(mapCategory)}
                            </ul>
                            <button className={css.close}>
                                <span>{getTranslation('close')}</span>
                            </button>
                        </div>
                    )}
                </div>
            </Wrapper>
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    NavigationSubcategory.propTypes = {
        categoryId: PropTypes.number.isRequired
    };
}

export default NavigationSubcategory;
